import { Button, TextField, Box } from '@mui/material';
import { DragModal, DragModalHeader } from 'components/DragModal/DragModal';
import { memo, useCallback, useState } from 'react';
import { isValidUrl } from 'utility/functions';
import { urlDialogActionsSx, urlDialogTextFieldSx } from './FileFieldInputStyles';
import { useTranslation } from 'react-i18next';

export type UrlInputDialogProps = {
  open: boolean;
  onClose: () => void;
  onSubmitUrl: (url: string) => void;
  customValidation?: (url: string) => boolean;
  customErrorMessage?: string;
};

export const UrlInputDialog = memo(
  ({
    open,
    onClose,
    onSubmitUrl,
    customValidation,
    customErrorMessage,
  }: UrlInputDialogProps) => {
    const [url, setUrl] = useState('');
    const [error, setError] = useState('');
    const { t } = useTranslation();

    const handleSubmit = useCallback(() => {
      if (isValidUrl(url) && (!customValidation || customValidation(url))) {
        onSubmitUrl(url);
        onClose();
        setError('');
      } else {
        if (!isValidUrl(url)) {
          setError('The URL is invalid, please check the input.');
        } else if (customValidation && !customValidation(url)) {
          setError(customErrorMessage || 'The URL is invalid, please check the input.');
        }
      }
    }, [url, customValidation, customErrorMessage, onSubmitUrl, onClose]);

    const handleDialogClose = useCallback(() => {
      setUrl(''); // Reset the url state.
      setError(''); // Reset the error state.
      onClose(); // Call the onClose prop.
    }, [onClose]);

    return (
      <DragModal
        id="upload-via-url-dialog"
        data-testid="upload-via-url-dialog"
        open={open}
        onClose={() => {
          handleDialogClose();
        }}
        initialWidth={600}
        initialHeight="auto"
        isDraggable={true}
        header={<DragModalHeader text={t('blox.general.provide_url')} />}
        body={
          <TextField
            data-testid="dialog-url-input"
            fullWidth
            autoFocus
            margin="dense"
            id="dialog-url"
            type="url"
            inputMode="url"
            label={t('blox.editorial.asset.url.label')}
            variant="outlined"
            autoComplete="off"
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              setError(''); // Reset error state on change
            }}
            aria-invalid={Boolean(error)}
            error={Boolean(error)}
            helperText={error}
            sx={urlDialogTextFieldSx}
          />
        }
        footer={
          <Box sx={urlDialogActionsSx}>
            <Button
              onClick={handleDialogClose}
              variant="outlined"
              data-testid="dialog-cancel-button"
            >
              {t('blox.actions.cancel')}
            </Button>
            <Button
              color="primary"
              onClick={handleSubmit}
              variant="contained"
              data-testid="dialog-submit-button"
              disableElevation
            >
              {t('blox.actions.submit')}
            </Button>
          </Box>
        }
      />
    );
  },
);
UrlInputDialog.displayName = 'UrlInputDialog';
